import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { useToast } from '../contexts/ToastContext';
import styled from 'styled-components';
import { ArrowLeft, Building2, User, Mail } from 'lucide-react';
import { apiService } from '../services/api';

const Container = styled.div`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #00d4aa 0%, #00b894 100%);
  padding: 20px;
`;

const Card = styled.div`
  background: white;
  border-radius: 12px;
  padding: 40px;
  width: 100%;
  max-width: 400px;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.1);
  position: relative;
`;

const BackButton = styled.button`
  position: absolute;
  top: 20px;
  left: 20px;
  display: flex;
  align-items: center;
  gap: 8px;
  color: #666;
  font-size: 14px;
  font-weight: 500;
  padding: 8px;
  border-radius: 6px;
  transition: all 0.2s ease;

  &:hover {
    background: #f8f9fa;
    color: #00d4aa;
  }
`;

const Title = styled.h1`
  text-align: center;
  margin-bottom: 8px;
  color: #333;
  font-size: 28px;
`;

const Subtitle = styled.p`
  text-align: center;
  color: #666;
  margin-bottom: 32px;
  font-size: 14px;
`;

const TabContainer = styled.div`
  display: flex;
  margin-bottom: 24px;
  border-radius: 8px;
  background: #f8f9fa;
  padding: 4px;
  gap: 4px;
`;

const Tab = styled.button<{ $active: boolean }>`
  flex: 1;
  padding: 12px;
  border-radius: 6px;
  font-weight: 500;
  font-size: 14px;
  transition: all 0.2s ease;
  background: ${props => props.$active ? 'white' : 'transparent'};
  color: ${props => props.$active ? '#00d4aa' : '#666'};
  box-shadow: ${props => props.$active ? '0 2px 4px rgba(0, 0, 0, 0.1)' : 'none'};
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const InputGroup = styled.div`
  position: relative;
`;

const Input = styled.input<{ $hasError?: boolean }>`
  width: 100%;
  padding: 16px;
  border: 2px solid ${props => props.$hasError ? '#dc3545' : '#e9ecef'};
  border-radius: 8px;
  font-size: 16px;
  transition: border-color 0.2s ease;

  &:focus {
    outline: none;
    border-color: #00d4aa;
  }

  &::placeholder {
    color: #adb5bd;
  }
`;

const Button = styled.button`
  width: 100%;
  padding: 16px;
  background: #00d4aa;
  color: white;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 600;
  transition: background-color 0.2s ease;

  &:hover:not(:disabled) {
    background: #00b894;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const ErrorMessage = styled.div`
  color: #dc3545;
  font-size: 14px;
  margin-top: 8px;
`;

const SuccessMessage = styled.div`
  color: #00d4aa;
  font-size: 14px;
  margin-top: 8px;
  text-align: center;
  padding: 12px;
  background: #d4f4e8;
  border-radius: 6px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;

  svg {
    color: #00d4aa;
  }

  p {
    margin: 0;
  }
`;

const Footer = styled.div`
  text-align: center;
  margin-top: 24px;
  padding-top: 24px;
  border-top: 1px solid #e9ecef;
  color: #666;
  font-size: 14px;
`;

const FooterLink = styled(Link)`
  color: #00d4aa;
  font-weight: 500;

  &:hover {
    text-decoration: underline;
  }
`;

interface FormData {
  email: string;
}

export const ForgotPasswordPage: React.FC = () => {
  const [userType, setUserType] = useState<'owner' | 'employee'>('owner');
  const [isLoading, setIsLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const { showError } = useToast();
  const navigate = useNavigate();
  const { register, handleSubmit, formState: { errors } } = useForm<FormData>();

  const onSubmit = async (data: FormData) => {
    setIsLoading(true);
    setSuccess(false);

    try {
      const response = userType === 'employee'
        ? await apiService.forgotPasswordEmployee(data.email)
        : await apiService.forgotPassword(data.email);

      if (response.success) {
        setSuccess(true);
      }
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || 'Erro ao processar solicitação';
      showError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Container>
      <Card>
        <BackButton type="button" onClick={() => navigate('/app/login')}>
          <ArrowLeft size={16} />
          Voltar
        </BackButton>

        <Title>Esqueceu a senha?</Title>
        <Subtitle>
          Digite seu e-mail e enviaremos um link para redefinir sua senha
        </Subtitle>

        <TabContainer>
          <Tab
            type="button"
            $active={userType === 'owner'}
            onClick={() => setUserType('owner')}
          >
            <Building2 size={16} />
            Proprietário
          </Tab>
          <Tab
            type="button"
            $active={userType === 'employee'}
            onClick={() => setUserType('employee')}
          >
            <User size={16} />
            Funcionário
          </Tab>
        </TabContainer>

        {success ? (
          <SuccessMessage>
            <Mail size={24} />
            <p>
              Se este e-mail estiver cadastrado, você receberá um link de redefinição de senha.
              Verifique sua caixa de entrada e spam.
            </p>
          </SuccessMessage>
        ) : (
          <Form onSubmit={handleSubmit(onSubmit)}>
            <InputGroup>
              <Input
                type="email"
                placeholder="E-mail"
                $hasError={!!errors.email}
                {...register('email', {
                  required: 'E-mail é obrigatório',
                  pattern: {
                    value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                    message: 'E-mail inválido'
                  }
                })}
              />
              {errors.email && <ErrorMessage>{errors.email.message}</ErrorMessage>}
            </InputGroup>

            <Button type="submit" disabled={isLoading}>
              {isLoading ? 'Enviando...' : 'Enviar Link de Redefinição'}
            </Button>
          </Form>
        )}

        <Footer>
          Lembrou sua senha?{' '}
          <FooterLink to="/app/login">Fazer login</FooterLink>
        </Footer>
      </Card>
    </Container>
  );
};
